import { AxisSwitcher, Hidden, ProductTile, SpacerWrapper } from '@ahmdigital/ui';
import { debounce, includes, map, omit } from 'lodash/fp';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Customer } from '../../../../../selectors/select-customer/types';
import { Prices } from '../../../../../models/price';
import { ProductType } from '../../../../../types/product';
import ActivityIndicator from './activity-indicator';
import browserUtil from '../../../../../utils/browser';
import constants from '../../../../../ahm-constants';
import prepareProductsForProductTiles from '../../../../../utils/prepare-products-for-product-tiles';
import styles from './styles.module.sass';
import updateTilePagination from './update-tile-pagination';
import useIsAfterHydration from '../../../../../hooks/use-is-after-hydration';

const { ADVANCED_HOSPITAL_GOLD_EXCESS_750 } = constants.PRODUCT;
const GOLD_HOSPITAL_PRODUCT_IDS = [ADVANCED_HOSPITAL_GOLD_EXCESS_750];

// @ts-expect-error - Automatic, Please fix when editing this file
const getProductTileProps = (isLoadingPrice, product, blurb) => {
  const { additionalDetails, ctaText, id, url } = product;

  return {
    additionalDetails,
    blurb,
    ctaText,
    id,
    isLoadingPrice,
    key: id,
    linkTo: url,
    variant: styles.productTile,
  };
};

// @ts-expect-error - Automatic, Please fix when editing this file
const getProductBlurb = (product) =>
  includes(product.id, GOLD_HOSPITAL_PRODUCT_IDS)
    ? 'All the gold category hospital services including pregnancy and birth.'
    : product?.content?.longForm;

const HorizontalScrollWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 1.5rem;
  overflow-y: hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  & > div {
    margin-right: 1rem;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const HorizontalScrollProductTile = styled(ProductTile)`
  scroll-snap-align: center;
  width: 16rem;

  @media (min-width: ${({ theme }) => theme.media.md}) {
    width: unset;
  }
`;

type ProductTilesProps = {
  isLoadingPrice?: boolean;
  products: ProductType[];
  withHorizontalScroll?: boolean;
  customer: Customer;
  prices: Prices;
  useFutureEffectiveDate: boolean;
};

const ProductTiles = ({
  isLoadingPrice = false,
  products,
  customer,
  prices,
  useFutureEffectiveDate,
  withHorizontalScroll = false,
}: ProductTilesProps): JSX.Element => {
  const ref = useRef(null);
  const [tilePagination, setTilePagination] = useState(null);
  const isAfterHydration = useIsAfterHydration();

  useEffect(() => {
    const tileContainer = ref.current;

    if (!tileContainer) {
      return () => null;
    }

    if (!tilePagination) {
      // @ts-expect-error - Automatic, Please fix when editing this file
      updateTilePagination(tileContainer, tilePagination, setTilePagination);
    }

    const debouncedUpdateTilePagination = debounce(150, () => {
      // @ts-expect-error - Automatic, Please fix when editing this file
      updateTilePagination(tileContainer, tilePagination, setTilePagination);
    });

    // @ts-expect-error - Automatic, Please fix when editing this file
    tileContainer.addEventListener('scroll', debouncedUpdateTilePagination);
    browserUtil.getWindow().addEventListener('resize', debouncedUpdateTilePagination);

    return () => {
      // @ts-expect-error - Automatic, Please fix when editing this file
      tileContainer.removeEventListener('scroll', debouncedUpdateTilePagination);
      browserUtil.getWindow().removeEventListener('resize', debouncedUpdateTilePagination);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // we don't need deps as we only need to attach the event listeners on first render, and remove on unmount

  return withHorizontalScroll ? (
    <SpacerWrapper variant="marginBottomSpacer6">
      <HorizontalScrollWrapper ref={ref}>
        {map(
          (product) => (
            <HorizontalScrollProductTile
              {...omit(['url', 'content'], product)}
              {...getProductTileProps(isLoadingPrice, product, getProductBlurb(product))}
            />
          ),
          prepareProductsForProductTiles(products, customer, prices, useFutureEffectiveDate),
        )}
      </HorizontalScrollWrapper>
      {isAfterHydration && (
        <Hidden df lg>
          {/* @ts-expect-error - Automatic, Please fix when editing this file */}
          {tilePagination && <ActivityIndicator {...tilePagination} />}
        </Hidden>
      )}
    </SpacerWrapper>
  ) : (
    <SpacerWrapper variant="marginBottomSpacer6">
      <AxisSwitcher limit={3} space="1rem" threshold="40rem">
        {map(
          (product) => (
            // @ts-expect-error - Automatic, Please fix when editing this file
            <div className={styles.productTileWrapper} key={product.url}>
              <ProductTile
                {...omit(['url', 'content'], product)}
                {...getProductTileProps(isLoadingPrice, product, getProductBlurb(product))}
              />
            </div>
          ),
          prepareProductsForProductTiles(products, customer, prices, useFutureEffectiveDate),
        )}
      </AxisSwitcher>
    </SpacerWrapper>
  );
};

export default ProductTiles;
